<template>
  <v-app
    id="app"
    :class="{ noFooter: isFooterVisible }"
  >
    <!-- Terms of Use Modal -->
    <TermsModal
      v-if="showTermsModal && isTermsOfUse"
      :show="showTermsModal"
      :message="getTermsMessage"
      @action="handleTermsAction"
    />
    <!-- Release Note Modal -->
    <MessageModal
      v-if="showMessageModal && isReleaseNote"
      :show="showMessageModal"
      :message="getReleaseMessage"
      :title="getReleaseTitle"
      :isReleaseNote="isReleaseNote"
      @close="
        isReleaseNote
          ? handleNoteClose($event)
          : handleSessionDestruction()
      "
    />
    <LockQueueModal />
    <Modal />
    <navbar
      v-if="isLoggedIn && !hideButtonAndNavbar"
      @openReleaseNotes="openReleaseNotes"
    />
    <v-content>
      <router-view />
    </v-content>
    <custom-footer v-if="isFooterVisible" />
    <div v-if="isChatActive && isLoggedIn && !hideButtonAndNavbar">
      <ChatOpen
        :isPopUpOpen="isPopUpOpen"
        @open-modal="showModal = true"
        @close-modal="showModal = false"
      />
      <ModalChat
        :isVisible="showModal"
        @open-modal-pupUp="isPopUpOpen = true"
        @close-modal-pupUp="isPopUpOpen = false"
      >
        <div slot="chatSlot">
          <GPT />
        </div>
      </ModalChat>
    </div>
    <Snackbar />
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ChatOpen from '@/components/chat/chat_circle.vue';
import ModalChat from '@/components/chat/modal.vue';
import GPT from '@/views/gpt/pages/GptChat.vue';
import Modal from '@/components/shared/forms-controls/Modal';
import MessageModal from '@/components/shared/MessageModal.vue';
import TermsModal from '@/components/shared/TermsOfUseModal.vue';
import LockQueueModal from '@/components/shared/LockQueueModal.vue';
import Snackbar from '@/components/snackbar/Snackbar.vue';
import { WEB } from '@/constants/platforms';
import { TYPE_GENAI } from '@/aquassist-shared/constants/types/features';
import { isFeatureInActiveFeatures } from '@/utilities/featureValidator';

export default {
    components: {
        Snackbar,
        Modal,
        MessageModal,
        GPT,
        ChatOpen,
        ModalChat,
        LockQueueModal,
        TermsModal,
    },
    data() {
        return {
            isChatActive: false,
            isFooterVisible: false,
            showMessageModal: false,
            showTermsModal: false,
            // Use separate flags for each message type
            isReleaseNote: false,
            isTermsOfUse: false,
            release: {},
            terms: {},
            showModal: false,
            isPopUpOpen: false,
            hideButtonAndNavbar: false,
        };
    },
    computed: {
        ...mapGetters([
            'isLoggedIn',
            'teamIndex',
            'defaultUserRoute',
            'currentMember',
            'displayNote',
            'isFirstLoginSession',
            'termsOfUseAccepted',
            'userLang',
            'teamID',
            'user',
        ]),
        combinedModalTrigger() {
            return {
                displayNote: this.displayNote,
                termsOfUseAccepted: this.termsOfUseAccepted,
                isFirstLogin: this.user.isFirstLogin,
            };
        },
        loginWatcher() {
            return this.isLoggedIn;
        },
        teamWatcher() {
            return this.teamIndex;
        },
        getReleaseMessage() {
            return this.release.note && this.release.note.message
                ? this.release.note.message[this.userLang]
                : this.$t('forceUpdate.message');
        },
        getReleaseTitle() {
            return this.release.title
                ? this.release.title[this.userLang]
                : '';
        },
        getTermsMessage() {
            return this.terms.description
                ? this.terms.description[this.userLang]
                : this.$t('forceUpdate.message');
        },
    },
    watch: {
        async loginWatcher(newValue) {
            if (newValue === false) {
                // don't redirect to login if we are in the nat routes
                const route = this.$router.currentRoute.matched[0];
                if (route && route.name !== 'nat') {
                    await this.$router.push({ name: 'login' });
                }
            } else {
                await this.$router.push({ name: this.defaultUserRoute });
            }
        },
        async teamWatcher(newValue) {
            // checking if the user is logged in, you can't call to renew token if they're not there
            // TODO: COMMIT HERE
            this.$store.commit('setIsStateAccessControlLoaded', {
                isStateAccessControlLoaded: false,
            });
            if (this.isLoggedIn) {
                await this.refreshAccessToken();
                await this.checkChatFeature();
                if (newValue !== -1) {
                    await this.$router.push({ name: this.defaultUserRoute });
                }
            }
        },
        $route: {
            handler() {
                switch (this.$route.name) {
                    case ('login', 'signup', 'PasswordReset', 'acceptInvite'):
                        break;
                    case 'gptChat':
                        this.hideButtonAndNavbar = true;
                        break;
                    case undefined:
                        this.isFooterVisible = false;
                        break;
                    default:
                        this.isFooterVisible = false;
                }
            },
            immediate: true,
        },
        combinedModalTrigger: {
            async handler() {
                if (!this.isFirstLoginSession) {
                    this.setFirstLoggedInSession(false);
                    return;
                }
                if (!this.termsOfUseAccepted && this.user.isFirstLogin) {
                    // Attempt to fetch Terms of Service info first
                    const { data: termsData } = await this.$rq.getTermsOfUse({ platformArg: WEB }); // TODO: whey fetch for web
                    if (termsData[WEB]?.id && termsData[WEB].displayInNextLoginTerms) {
                        this.terms = termsData[WEB];
                        this.showTermsModal = true;
                        this.isTermsOfUse = true;
                    }
                } else if (this.displayNote) {
                    // Fallback to Release Note info
                    await this.getReleaseInfo();
                    if (this.release.id && this.release.platform === WEB && this.release.displayInNextLogin) {
                        this.openReleaseNotes();
                    }
                }
                this.setFirstLoggedInSession(false);
            },
            immediate: true,
        },

    },
    async created() {
        this.setLang();
        if (this.isLoggedIn) {
            await this.refreshAccessToken();
            await this.checkChatFeature();
        }
    },
    mounted() {
        this.$fetchPeriodically();
    },
    methods: {
        ...mapActions({
            setLang: 'setLang',
            setFirstLoggedInSession: 'setFirstLoggedInSession',
            refreshAccessToken: 'refreshAccessTokenAndReloadAccessControlState',
            getFeaturesInfo: 'getFeaturesInfo',
            logout: 'logout',
        }),
        async checkChatFeature() {
            const { data: team } = await this.$rq.getTeam(this.teamID);
            const isChatActive = isFeatureInActiveFeatures(team, TYPE_GENAI);
            this.isChatActive = isChatActive;
        },
        processFetchedData(data) {
            if (data !== null && data.data.forceUpdate) {
                this.showMessageModal = true;
            }
        },
        async handleSessionDestruction() {
            localStorage.clear();
            this.$cookies.remove('accessToken');
            this.$cookies.remove('refreshToken');
            this.showMessageModal = false;
            this.showTermsModal = false;
            this.isReleaseNote = false;
            this.isTermsOfUse = false;
            this.$router.push('/login');
        },
        async getReleaseInfo() {
            const { data } = await this.$rq.getRelease({ platform: WEB });
            if (data[WEB]?.id && data[WEB].displayInNextLogin) {
                this.release = data[WEB];
            }
        },
        async handleNoteClose(dontShowAgain) {
            await this.$rq.updateMember(this.currentMember.id, {
                displayNote: { displayNote: !dontShowAgain, platform: WEB },
            });
            this.showMessageModal = false;
            this.isReleaseNote = false;
        },
        async handleTermsAction(action) {
            if (action === 'accept') {
                await this.$rq.acknowledgeMemberTerms(this.currentMember.id, { termsOfUseAccepted: true });
                this.isTermsOfUse = false;
                this.showTermsModal = false;
                if (this.displayNote) {
                    if (this.release.id && this.release.platform === WEB && this.release.displayInNextLogin) {
                        this.openReleaseNotes();
                    }
                }
            } else if (action === 'decline') {
                this.logout();

                localStorage.clear();
                this.showMessageModal = false;
                this.showTermsModal = false;
                this.isReleaseNote = false;
                this.isTermsOfUse = false;
            }
        },
        async openReleaseNotes() {
            await this.getReleaseInfo();
            this.isReleaseNote = true;
            this.showMessageModal = true;
        },
    },
};
</script>

    <style lang="scss">
    @import '@/assets/styles/styles.scss';
    .theme--light.v-application {
      background-color: #2a3e53;
      padding-left: 6px;
      padding-right: 6px;
    }
    .v-content__wrap {
      background-color: white;
    }
    </style>
